import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Logger } from '../utils/log-util';

@Injectable({
    providedIn: 'root',
})
export class GTMService {

    constructor() {}

    /**
     * Check if GTM should fire (only in production)
     */
    private shouldFireGTM(): boolean {
        return ["prod"].includes(environment.appEnv);
    }

    /**
     * Push data to Google Tag Manager dataLayer
     * Only fires in production environment (appEnv='prod')
     * @param data - Object containing GTM event data
     */
    push(data: any) {
        Logger.debug('[GTM] Event data:', data);
        if (!this.shouldFireGTM()) {
            Logger.warn('[GTM - UAT/Dev Mode] Event not fired:', data);
            return;
        }

        try {
            (<any>window).dataLayer = (<any>window).dataLayer || [];
            (<any>window).dataLayer.push(data);
        } catch (e) {
            Logger.error('GTM push error:', e);
        }
    }

    /**
     * Push event with callback - useful for tracking before navigation
     * Only fires in production environment (appEnv='prod')
     * @param data - Event data
     * @param callback - Function to execute after push
     * @param timeout - Max time to wait for GTM (default 1000ms)
     */
    pushWithCallback(data: any, callback: Function, timeout: number = 1000) {
        if (!this.shouldFireGTM()) {
            Logger.warn('[GTM - Dev Mode] Event with callback not fired:', data);
            callback(); // Execute callback immediately in non-prod
            return;
        }

        try {
            const eventTimeout = setTimeout(() => {
                callback();
            }, timeout);

            this.push({
                ...data,
                eventCallback: () => {
                    clearTimeout(eventTimeout);
                    callback();
                },
                eventTimeout: timeout
            });
        } catch (e) {
            Logger.error('GTM pushWithCallback error:', e);
            callback(); // Ensure callback is called even on error
        }
    }

    /**
     * Disable default GTM click tracking for specific elements
     * Useful when you want to track custom events instead
     */
    disableAutoClickTracking(selector: string) {
        if (!this.shouldFireGTM()) {
            Logger.warn('[GTM - Dev Mode] Auto-click tracking disable skipped for:', selector);
            return;
        }

        try {
            document.querySelectorAll(selector).forEach((element) => {
                element.addEventListener('click', (event) => {
                    event.stopImmediatePropagation();
                }, true);
            });
        } catch (e) {
            Logger.error('GTM disableAutoClickTracking error:', e);
        }
    }
}
